/**
 * Timeline filter for the WAF analytics pages. Wraps `WafDateTimeRangePicker`
 * (quick-select presets + a calendar with per-endpoint date/time inputs) and
 * exposes the resolved `{ start_time, end_time }` (unix seconds) its consumers
 * (DNS / DEM / Pulse) already query with. Presets resolve against "now" on load;
 * custom ranges persist verbatim.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  WafDateTimeRangePicker,
  PRESETS,
  presetRange,
  toISO,
  type TimeRange,
} from './WafDateTimeRangePicker';
import { loadJSON, saveJSON } from './storage';

export interface ResolvedRange {
  start_time: number;
  end_time: number;
}

interface RangeState {
  /** Active preset value (e.g. "7d"); set when not a custom range. */
  preset?: string;
  /** Custom-range ISO bounds (set instead of `preset`). */
  from?: string;
  to?: string;
}

const DEFAULT_PRESET = '7d';
const toSec = (iso: string) => Math.floor(new Date(iso).getTime() / 1000);

function resolve(state: RangeState): ResolvedRange {
  if (state.from && state.to) {
    const a = new Date(state.from);
    const b = new Date(state.to);
    if (!Number.isNaN(a.getTime()) && !Number.isNaN(b.getTime())) {
      return { start_time: toSec(state.from), end_time: toSec(state.to) };
    }
  }
  const r = presetRange(state.preset ?? DEFAULT_PRESET);
  return { start_time: Math.floor(r.from.getTime() / 1000), end_time: Math.floor(r.to.getTime() / 1000) };
}

/** The committed selection as the picker's `TimeRange` value. */
function toTimeRange(state: RangeState): TimeRange {
  if (state.from && state.to) return { from: state.from, to: state.to, label: 'Custom' };
  const p = PRESETS.find((x) => x.value === (state.preset ?? DEFAULT_PRESET)) ?? PRESETS[5];
  const r = presetRange(p.value);
  return { from: toISO(r.from), to: toISO(r.to), label: p.label };
}

export function WafRangeControl({
  serviceId,
  pageKey,
  onChange,
}: {
  serviceId: string;
  pageKey: string;
  onChange: (range: ResolvedRange) => void;
}) {
  const storeKey = `waf:${serviceId}:${pageKey}:range`;
  const [state, setState] = useState<RangeState>(() =>
    loadJSON<RangeState>(storeKey, { preset: DEFAULT_PRESET }),
  );

  const range = useMemo(() => resolve(state), [state]);
  const value = useMemo(() => toTimeRange(state), [state]);

  // Notify the parent whenever the resolved window changes.
  useEffect(() => {
    onChange(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.start_time, range.end_time]);

  const apply = (r: TimeRange) => {
    const preset = r.label !== 'Custom' ? PRESETS.find((p) => p.label === r.label)?.value : undefined;
    const next: RangeState = preset ? { preset } : { from: r.from, to: r.to };
    saveJSON(storeKey, next);
    setState(next);
  };

  return <WafDateTimeRangePicker value={value} onApply={apply} />;
}

export default WafRangeControl;
